import { defineStore } from "pinia"
import { ref } from "vue"

import axios from "@axios"
import { reportError } from "@/plugins/misc"

const baseUrl = '/agent/admin/'

export const useAgentAdminStore = defineStore('agentAdminStore', () => {
  const conversations = ref([])
  const total = ref(0)
  const page = ref(1)
  const pages = ref(0)
  const loading = ref(false)

  const activeConversation = ref(null)
  const activeConversationLoading = ref(false)

  // RM-208: annotations on the active conversation, keyed for quick lookup by
  // the annotated AI message's db index.
  const activeFeedback = ref([])

  const stats = ref(null)

  // RM-316: restaurant team people's texts with the RM team, one thread per person.
  const restaurantThreads = ref([])
  const restaurantThreadsLoading = ref(false)

  async function getRestaurantThreads() {
    restaurantThreadsLoading.value = true
    try {
      const res = await axios.get(`${baseUrl}restaurant-threads`, {
        params: { jwt: JSON.parse(localStorage.getItem('jwt')) },
      })
      restaurantThreads.value = res.data?.threads || []
    } catch (error) {
      reportError('agentAdmin.store.js:getRestaurantThreads', error.message, null, null, null, error)
    } finally {
      restaurantThreadsLoading.value = false
    }
  }

  async function getConversations(params = {}) {
    loading.value = true
    try {
      const res = await axios.get(`${baseUrl}conversations`, {
        params: {
          jwt: JSON.parse(localStorage.getItem('jwt')),
          page: params.page || 1,
          limit: params.limit || 25,
          search: params.search || undefined,
          status: params.status || undefined,
          support: params.support || undefined,
        }
      })

      if (res.data) {
        conversations.value = res.data.conversations
        total.value = res.data.total
        page.value = res.data.page
        pages.value = res.data.pages
      }
    } catch (error) {
      console.error(error)
      reportError("agentAdmin.store.js:getConversations", error.message, null, null, null, error)
    } finally {
      loading.value = false
    }
  }

  async function getConversation(conversationId) {
    activeConversationLoading.value = true
    try {
      const res = await axios.get(`${baseUrl}conversations/${conversationId}`, {
        params: {
          jwt: JSON.parse(localStorage.getItem('jwt')),
        }
      })

      if (res.data) {
        activeConversation.value = res.data
      }
    } catch (error) {
      console.error(error)
      reportError("agentAdmin.store.js:getConversation", error.message, null, null, null, error)
    } finally {
      activeConversationLoading.value = false
    }

    // Load any existing annotations for this conversation (non-blocking on the
    // transcript render).
    await getConversationFeedback(conversationId)
  }

  async function getConversationFeedback(conversationId) {
    try {
      const res = await axios.get(`${baseUrl}prompt-feedback`, {
        params: {
          jwt: JSON.parse(localStorage.getItem('jwt')),
          conversationId,
          limit: 100,
        }
      })
      activeFeedback.value = res.data?.feedback || []
    } catch (error) {
      console.error(error)
      activeFeedback.value = []
    }
  }

  async function saveFeedback(conversationId, payload) {
    const res = await axios.post(`${baseUrl}conversations/${conversationId}/feedback`, payload, {
      params: { jwt: JSON.parse(localStorage.getItem('jwt')) },
    })
    if (res.data?.feedback) {
      activeFeedback.value = [res.data.feedback, ...activeFeedback.value]
    }

    return res.data?.feedback || null
  }

  // RM-208: standalone annotations list (admin-annotations page), independent
  // of the per-conversation activeFeedback state.
  const feedbackList = ref([])
  const feedbackTotal = ref(0)
  const feedbackPages = ref(0)
  const feedbackLoading = ref(false)

  async function getFeedbackList(params = {}) {
    feedbackLoading.value = true
    try {
      const res = await axios.get(`${baseUrl}prompt-feedback`, {
        params: {
          jwt: JSON.parse(localStorage.getItem('jwt')),
          page: params.page || 1,
          limit: params.limit || 25,
          status: params.status || undefined,
          category: params.category || undefined,
          severity: params.severity || undefined,
        }
      })
      feedbackList.value = res.data?.feedback || []
      feedbackTotal.value = res.data?.total || 0
      feedbackPages.value = res.data?.pages || 0
    } catch (error) {
      console.error(error)
      reportError("agentAdmin.store.js:getFeedbackList", error.message, null, null, null, error)
    } finally {
      feedbackLoading.value = false
    }
  }

  async function updateFeedback(id, patch) {
    const res = await axios.patch(`${baseUrl}prompt-feedback/${id}`, patch, {
      params: { jwt: JSON.parse(localStorage.getItem('jwt')) },
    })
    if (res.data?.feedback) {
      const i = activeFeedback.value.findIndex(f => f._id === id)
      if (i !== -1) activeFeedback.value[i] = res.data.feedback
      const j = feedbackList.value.findIndex(f => f._id === id)
      if (j !== -1) feedbackList.value[j] = res.data.feedback
    }

    return res.data?.feedback || null
  }

  async function getStats() {
    try {
      const res = await axios.get(`${baseUrl}stats`, {
        params: {
          jwt: JSON.parse(localStorage.getItem('jwt')),
        }
      })

      if (res.data) {
        stats.value = res.data
      }
    } catch (error) {
      console.error(error)
      reportError("agentAdmin.store.js:getStats", error.message, null, null, null, error)
    }
  }

  async function getTrace(conversationId, msgIndex) {
    try {
      const res = await axios.get(`/agent/conversations/${conversationId}/trace`, {
        params: {
          jwt: JSON.parse(localStorage.getItem('jwt')),
          msgIndex,
        }
      })

      return res.data?.traceData || null
    } catch (error) {
      console.error(error)
      
      return null
    }
  }

  function clearActiveConversation() {
    activeConversation.value = null
    activeFeedback.value = []
  }

  // Transcript export (Kapil, 2026-09-16): the server builds one readable transcript — member + RM AI
  // messages with times and the tools each reply ran — as plain text (Copy) or a PDF (Download).
  // The auth header comes from the axios interceptor; the token never goes in the URL.
  const exportTimeZone = () => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
    } catch (e) {
      return undefined
    }
  }

  async function fetchConversationText(conversationId) {
    const res = await axios.get(`${baseUrl}conversations/${conversationId}/export`, {
      params: { format: 'text', tz: exportTimeZone() },
      responseType: 'text',
      transformResponse: [data => data],
    })

    return res.data
  }

  async function downloadConversationPdf(conversationId) {
    const res = await axios.get(`${baseUrl}conversations/${conversationId}/export`, {
      params: { format: 'pdf', tz: exportTimeZone() },
      responseType: 'blob',
    })
    const disposition = res.headers?.['content-disposition'] || ''
    const match = /filename="([^"]+)"/.exec(disposition)
    const filename = match ? match[1] : 'RM AI conversation.pdf'
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(url), 10000)

    return filename
  }

  // RM-205 member support: the words, exactly as written, into the conversation and over the
  // member's channel. RM-441: answering one member message (memberMsgDbIndex, else the most
  // recent) inside the follow-up. Returns the channel it went out on.
  async function sendSupportReply(conversationId, text, memberMsgDbIndex = null) {
    const res = await axios.post(`${baseUrl}conversations/${conversationId}/support-reply`, { text, memberMsgDbIndex }, {
      params: { jwt: JSON.parse(localStorage.getItem('jwt')) },
    })
    await getConversation(conversationId)
    
    return res.data
  }

  // RM-441: exactly what the reply would send (by text or email, to whom, the words), and the
  // member messages the admin can answer. Sends nothing.
  async function previewSupportReply(conversationId, text, memberMsgDbIndex = null) {
    const res = await axios.post(`${baseUrl}conversations/${conversationId}/support-reply/preview`, { text, memberMsgDbIndex }, {
      params: { jwt: JSON.parse(localStorage.getItem('jwt')) },
    })
    
    return res.data
  }

  return {
    restaurantThreads,
    restaurantThreadsLoading,
    getRestaurantThreads,
    sendSupportReply,
    previewSupportReply,
    fetchConversationText,
    downloadConversationPdf,
    conversations,
    total,
    page,
    pages,
    loading,
    activeConversation,
    activeConversationLoading,
    activeFeedback,
    feedbackList,
    feedbackTotal,
    feedbackPages,
    feedbackLoading,
    stats,
    getConversations,
    getConversation,
    getConversationFeedback,
    getFeedbackList,
    saveFeedback,
    updateFeedback,
    getStats,
    getTrace,
    clearActiveConversation,
  }
})
